import { NetworkError } from '@recipes/api-client';
import {
  NUTRIENT_KEYS,
  labelExtractionResultSchema,
  type LabelExtractionResult,
  type NutriScoreGrade,
  type ProductUpdateInput,
} from '@recipes/contracts';
import { Alert, Button, Input, Label, Spinner, Textarea, cn } from '@recipes/ui';
import { AlertTriangle, Camera, ScanText } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useGeneration } from '@/features/ai/use-generation';
import { useAiConsent, useFeature } from '@/features/shared/hooks';
import { ProcessingIndicator } from '@/features/shared/processing-indicator';
import { api } from '@/lib/api';
import { describeWorkerError, toastError } from '@/lib/errors';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import { NutriScoreBadge } from './badges';

export type LabelValues = Pick<
  ProductUpdateInput,
  | 'ingredientsText'
  | 'allergens'
  | 'additives'
  | 'nutrition'
  | 'nutriScore'
  | 'packageQuantity'
  | 'packageUnitText'
  | 'dataSource'
>;

async function uploadLabelPhoto(file: File): Promise<string> {
  const intent = await api.media.createUploadIntent({
    fileName: file.name || 'label.jpg',
    mimeType: file.type || 'image/jpeg',
    sizeBytes: file.size,
    // Label photos are AI inputs: they expire with AI_INPUT_RETENTION_HOURS.
    purpose: 'AI_INPUT',
  });
  try {
    await api.media.upload(intent, file);
  } catch (error) {
    throw error instanceof TypeError ? new NetworkError(error) : error;
  }
  return (await api.media.complete(intent.assetId, { altText: null })).id;
}

/** Review of a label scan next to nothing but the facts: unreadable parts are marked, nothing is guessed. */
function LabelReview({
  result,
  onApply,
  onDiscard,
}: {
  result: LabelExtractionResult;
  onApply: (values: LabelValues) => void;
  onDiscard: () => void;
}) {
  const { t } = useTranslation();
  const ids = { text: useId() };
  const [ingredientsText, setIngredientsText] = useState(result.ingredientsText ?? '');
  const [rows, setRows] = useState(() =>
    Object.fromEntries(result.nutrition.map((r) => [r.nutrient, r.per100 ?? ''])),
  );
  const [nutriScore, setNutriScore] = useState<NutriScoreGrade | null>(result.nutriScore);
  const flagged = useMemo(
    () => new Map(result.nutrition.filter((r) => r.implausible).map((r) => [r.nutrient, r.reason])),
    [result.nutrition],
  );

  const apply = () => {
    const nutrition: Record<string, string | null> = {};
    for (const key of NUTRIENT_KEYS) {
      const raw = String(rows[key] ?? '')
        .trim()
        .replace(',', '.');
      if (raw && /^\d+(\.\d+)?$/.test(raw)) nutrition[key] = raw;
    }
    onApply({
      ingredientsText: ingredientsText.trim() || null,
      allergens: result.allergens,
      additives: result.additives,
      nutrition: Object.keys(nutrition).length ? { basis: result.basis, ...nutrition } : undefined,
      ...(nutriScore ? { nutriScore } : {}),
      ...(result.packageQuantity ? { packageQuantity: result.packageQuantity } : {}),
      ...(result.packageUnitText ? { packageUnitText: result.packageUnitText } : {}),
      dataSource: 'LABEL_SCAN',
    });
  };

  return (
    <div className="space-y-3 rounded-lg border p-3" data-testid="label-review">
      <h3 className="font-semibold">{t('catalog.label.reviewTitle')}</h3>
      <p className="text-xs text-muted-foreground">{t('catalog.label.reviewHint')}</p>

      <div>
        <Label htmlFor={ids.text}>{t('catalog.product.ingredientsText')}</Label>
        <Textarea
          id={ids.text}
          className="mt-1"
          rows={4}
          maxLength={4000}
          value={ingredientsText}
          onChange={(e) => setIngredientsText(e.target.value)}
          data-testid="label-ingredients"
        />
        {result.allergens.length > 0 && (
          <p className="mt-1 text-xs">
            <span className="font-medium">{t('catalog.product.allergens')}:</span>{' '}
            {result.allergens.join(', ')}
          </p>
        )}
      </div>

      <fieldset>
        <legend className="mb-1 text-sm font-medium">
          {t(`catalog.nutrition.${result.basis}`)}
        </legend>
        <div className="grid grid-cols-2 gap-2">
          {NUTRIENT_KEYS.filter((key) => key in rows).map((key) => (
            <div key={key}>
              <Label htmlFor={`label-${key}`} className="text-xs">
                {t(`catalog.nutrient.${key}`)}
              </Label>
              <Input
                id={`label-${key}`}
                inputMode="decimal"
                value={rows[key] ?? ''}
                aria-invalid={flagged.has(key)}
                aria-describedby={flagged.has(key) ? `label-${key}-flag` : undefined}
                className={cn('mt-0.5', flagged.has(key) && 'border-warning')}
                onChange={(e) => setRows((prev) => ({ ...prev, [key]: e.target.value }))}
                data-testid={`label-row-${key}`}
              />
              {flagged.has(key) && (
                <p
                  id={`label-${key}-flag`}
                  className="mt-0.5 flex items-center gap-1 text-xs text-warning-foreground"
                  data-testid={`label-flag-${key}`}
                >
                  <AlertTriangle className="size-3" aria-hidden />
                  {t(`catalog.label.flag.${flagged.get(key)}`)}
                </p>
              )}
            </div>
          ))}
        </div>
      </fieldset>

      {result.nutriScore && (
        <div className="flex items-center gap-2 text-sm">
          <NutriScoreBadge grade={result.nutriScore} size="sm" />
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={nutriScore !== null}
              onChange={(e) => setNutriScore(e.target.checked ? result.nutriScore : null)}
            />
            {t('catalog.label.useNutriScore')}
          </label>
        </div>
      )}

      {result.unreadable.length > 0 && (
        <Alert variant="warning" data-testid="label-unreadable">
          {t('catalog.label.unreadable', { parts: result.unreadable.join(', ') })}
        </Alert>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onDiscard}>
          {t('catalog.label.discard')}
        </Button>
        <Button type="button" onClick={apply} data-testid="label-apply">
          {t('catalog.label.apply')}
        </Button>
      </div>
    </div>
  );
}

/**
 * Scan the ingredients label (docs/finished/ingredient-catalog-spec.md 5.3): photos → AI extraction →
 * review → the caller stores what the user accepted. Needs a connection and AI consent;
 * offline the button explains why it is not available.
 */
export function LabelScan({
  productId,
  onApply,
}: {
  productId: string | null;
  onApply: (values: LabelValues) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const aiEnabled = useFeature('ai');
  const consented = useAiConsent();
  const online = useOnlineStatus();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [generationId, setGenerationId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const { generation } = useGeneration(generationId);

  const result = useMemo(() => {
    if (generation?.status !== 'SUCCEEDED') return null;
    const parsed = labelExtractionResultSchema.safeParse(generation.result);
    return parsed.success ? parsed.data : null;
  }, [generation]);
  useEffect(() => setDismissed(false), [generationId]);

  if (!aiEnabled) return null;

  const start = async (files: FileList | null) => {
    const picked = [...(files ?? [])].slice(0, 3);
    if (picked.length === 0) return;
    setUploading(true);
    try {
      const assetIds: string[] = [];
      for (const file of picked) assetIds.push(await uploadLabelPhoto(file));
      const created = await api.products.labelScan(productId, { assetIds }, crypto.randomUUID());
      setGenerationId(created.id);
    } catch (error) {
      toastError(error);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const running =
    uploading || (generation && !['SUCCEEDED', 'FAILED', 'CANCELLED'].includes(generation.status));

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        onChange={(e) => void start(e.target.files)}
        data-testid="label-scan-input"
      />
      <Button
        type="button"
        variant="outline"
        disabled={!online || Boolean(running)}
        onClick={() => (consented ? inputRef.current?.click() : navigate('/profile'))}
        data-testid="label-scan-start"
      >
        {running ? <Spinner label={t('catalog.label.running')} /> : <ScanText aria-hidden />}
        {consented ? t('catalog.label.scan') : t('catalog.label.needsConsent')}
      </Button>
      {running && (
        <ProcessingIndicator
          label={t('catalog.label.running')}
          progress={generation?.status === 'RUNNING' ? generation.progress : null}
        />
      )}
      <p className="flex items-center gap-1 text-xs text-muted-foreground">
        <Camera className="size-3.5" aria-hidden />
        {online ? t('catalog.label.hint') : t('catalog.label.offline')}
      </p>
      {generation?.status === 'FAILED' && (
        <Alert variant="destructive">
          {generation.errorCode
            ? describeWorkerError(generation.errorCode, generation.error)
            : t('catalog.label.failed')}
        </Alert>
      )}
      {result && !dismissed && (
        <LabelReview
          result={result}
          onDiscard={() => setDismissed(true)}
          onApply={(values) => {
            onApply(values);
            setDismissed(true);
          }}
        />
      )}
    </div>
  );
}
